import { maybe } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import type { DashboardApi } from 'v5/api/dashboard/dashboard.api';
import { ensureIdleQuery } from 'v5/platform/ensureIdleQuery';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { DashboardService } from './DashboardService';

export function createDashboardService(
  config: WithServices<[WithErrorService]> & {
    api: DashboardApi;
    queryClient: QueryClient;
  }
): DashboardService {
  const {
    api,
    queryClient,
    services: { errorService }
  } = config;
  const interceptor = errorService.withCategory('DashboardService');

  return {
    useDashboard: input =>
      ensureIdleQuery(
        input,
        useQuery(
          {
            enabled: maybe.isPresent(input),
            queryKey: ['dashboard', input],
            // A read across productions, bookings and callsheets that no
            // mutation of theirs invalidates: fetched again on every visit and
            // on a return to the tab, the last result shown meanwhile.
            refetchOnMount: 'always',
            staleTime: 60_000,
            queryFn: interceptor.wrap(({ signal }) =>
              maybe.map(input, value => api.get(value, signal))
            )
          },
          queryClient
        )
      )
  };
}
